// src/pages/RecommendationsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Grid,
  Avatar,
  Alert,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  CircularProgress,
  Pagination,
  Card,
  CardContent,
  CardActions,
  Drawer,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Groups as GroupsIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  AutoAwesome as AutoAwesomeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import InviteModal from '../components/InviteModal/InviteModal';
import UserCard from '../components/UserCard/UserCard';
import { authAPI, projectsAPI } from '../services/api';

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================

const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  paddingTop: 0,
  paddingBottom: theme.spacing(6),
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));

const AnimatedHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '960px',
  minWidth: '640px',
  minHeight: 300,
  marginTop: '-100px',
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(3),
  background: 'linear-gradient(135deg, #284b63 0%, #3c6e71 50%, #58b6b9 100%)',
  animation: 'AnimateBG 10s ease infinite',
  backgroundSize: '300% 300%',
  overflow: 'hidden',
  boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.2)',
  '@keyframes AnimateBG': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' }
  },
  [theme.breakpoints.down('md')]: {
    width: 'calc(100% - 32px)',
    minWidth: 'auto',
    marginLeft: '16px',
    marginRight: '16px'
  }
}));

const CubesContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 1
});

const Cube = styled(Box)(({ delay, size, left }) => ({
  position: 'absolute',
  display: 'block',
  listStyle: 'none',
  width: size || '20px',
  height: size || '20px',
  background: 'rgba(255, 255, 255, 0.15)',
  bottom: '-150px',
  left: left || 'auto',
  animation: 'animate 25s ease-in infinite',
  animationDelay: delay || '0s',
  '@keyframes animate': {
    '0%': { transform: 'translateY(0) rotate(0deg)', opacity: 1, borderRadius: 0 },
    '100%': { transform: 'translateY(-1000px) rotate(720deg)', opacity: 0, borderRadius: '50%' }
  }
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: 110,
  zIndex: 2,
  padding: theme.spacing(2, 5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  color: theme.palette.common.white
}));

const StatChip = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(3),
  backgroundColor: 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(4px)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 960,
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const ShowMoreButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontWeight: 500
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2)
}));

const SkeletonCard = ({ variant = 'simple' }) => {
  const isRecommendation = variant === 'recommendation';

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' },
        maxWidth: 300,
        mx: 'auto',
        display: 'flex'
      }}
    >
      <Card
        sx={{
          width: '100%',
          minHeight: 380,
          height: '100%',
          overflow: 'hidden',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: (theme) => alpha(theme.palette.background.paper, 0.72),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 12px 35px rgba(0,0,0,0.06)'
        }}
      >
        {/* Шапка — повторяет реальную UserCard */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Skeleton
            variant="circular"
            width={64}
            height={64}
            sx={{ flexShrink: 0 }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="rounded" width="78%" height={21} />
            <Skeleton variant="rounded" width="58%" height={16} sx={{ mt: 0.8 }} />
            <Skeleton variant="rounded" width="72%" height={13} sx={{ mt: 0.9 }} />
          </Box>
        </Box>

        <CardContent
          sx={{
            px: 2,
            py: 1.75,
            minHeight: 226,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {isRecommendation && (
            <Box
              sx={{
                mb: 1.75,
                p: 1.25,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.7 }}>
                <Skeleton variant="rounded" width="42%" height={14} />
                <Skeleton variant="rounded" width={28} height={14} />
              </Box>
              <Skeleton variant="rounded" width="100%" height={6} sx={{ borderRadius: 999 }} />
            </Box>
          )}

          <Skeleton variant="rounded" width={52} height={13} sx={{ mb: 0.8 }} />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            <Skeleton variant="rounded" width={58} height={24} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="rounded" width={82} height={24} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="rounded" width={64} height={24} sx={{ borderRadius: 1.5 }} />
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Skeleton variant="rounded" width={105} height={13} sx={{ mt: 2, mb: 0.8 }} />
            <Box sx={{ display: 'flex', gap: 0.6 }}>
              <Skeleton variant="rounded" width={88} height={24} sx={{ borderRadius: 1.5 }} />
              <Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: 1.5 }} />
            </Box>
          </Box>
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={40}
            sx={{ borderRadius: 2.5 }}
          />
        </CardActions>
      </Card>
    </Box>
  );
};

const cubes = [
  { left: '5%', size: '60px', delay: '0s' },
  { left: '15%', size: '25px', delay: '2s' },
  { left: '25%', size: '40px', delay: '4s' },
  { left: '35%', size: '80px', delay: '1s' },
  { left: '45%', size: '20px', delay: '3s' },
  { left: '55%', size: '70px', delay: '5s' },
  { left: '65%', size: '35px', delay: '2s' },
  { left: '75%', size: '50px', delay: '6s' },
  { left: '85%', size: '30px', delay: '4s' },
  { left: '95%', size: '45px', delay: '0s' },
];

const API_BASE = 'https://fincollab.ru/api/auth';
const RECOMMENDATIONS_STORAGE_KEY = (() => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return 'recommendationsPageData:anonymous';
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const userId = payload.user_id ?? payload.userId ?? payload.sub ?? 'authenticated';
    return `recommendationsPageData:${userId}`;
  } catch (_) {
    return localStorage.getItem('access_token') ? 'recommendationsPageData:authenticated' : 'recommendationsPageData:anonymous';
  }
})();

const RecommendationsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [recommendations, setRecommendations] = useState(() => { try { const d = JSON.parse(sessionStorage.getItem(RECOMMENDATIONS_STORAGE_KEY) || 'null'); return d && Date.now() - d.timestamp < 180000 ? (d.recommendations || []) : []; } catch (_) { return []; } });
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(() => { try { const d = JSON.parse(sessionStorage.getItem(RECOMMENDATIONS_STORAGE_KEY) || 'null'); return !(d && Date.now() - d.timestamp < 180000); } catch (_) { return true; } });
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('match');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [branchFilter, setBranchFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);

  const [branchesFromDB, setBranchesFromDB] = useState([]);
  const [competenciesFromDB, setCompetenciesFromDB] = useState([]);
  const [researchFieldsFromDB, setResearchFieldsFromDB] = useState([]);

  const [allUsersPage, setAllUsersPage] = useState(1);
  const [allUsersTotalPages, setAllUsersTotalPages] = useState(1);
  const [allUsersCount, setAllUsersCount] = useState(0);

  const [showAllRecommendations, setShowAllRecommendations] = useState(false);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [checkingProfile, setCheckingProfile] = useState(true);
  const [needsQuestionnaire, setNeedsQuestionnaire] = useState(false);

  // 🔥 Загрузка справочников из БД
  useEffect(() => {
    const loadDictionaries = async () => {
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      try {
        const [branches, competencies, researchFields] = await Promise.all([
          fetch(`${API_BASE}/branches/`, { headers }).then(r => r.json()),
          fetch(`${API_BASE}/competencies/`, { headers }).then(r => r.json()),
          fetch(`${API_BASE}/research-fields/`, { headers }).then(r => r.json()),
        ]);
        setBranchesFromDB(branches || []);
        setCompetenciesFromDB(competencies || []);
        setResearchFieldsFromDB(researchFields || []);
      } catch (err) {
        console.error('❌ Ошибка загрузки справочников:', err);
      }
    };
    loadDictionaries();
  }, []);

  // 🔥 Загрузка рекомендаций
  const loadRecommendations = useCallback(async () => {
    try {
      const fresh = (() => { try { const d = JSON.parse(sessionStorage.getItem(RECOMMENDATIONS_STORAGE_KEY) || 'null'); return Boolean(d && Date.now() - d.timestamp < 180000); } catch (_) { return false; } })();
      if (!fresh) setLoading(true);
      setError('');
      const response = await projectsAPI.getUserRecommendations();

      if (response?.needs_questionnaire) {
        setNeedsQuestionnaire(true);
        setRecommendations([]);
        return;
      }

      let data = [];
      if (response?.recommendations) {
        data = response.recommendations;
      } else if (Array.isArray(response)) {
        data = response;
      }

      const filtered = data.filter(item => {
        const role = (item.role || '').toLowerCase();
        return !role.includes('admin');
      });

      const formatted = filtered.map(item => ({
        id: item.id,
        email: item.email,
        first_name: item.first_name || '',
        last_name: item.last_name || '',
        middle_name: item.middle_name || '',
        full_name: item.full_name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Пользователь',
        role: item.role || 'user',
        branch: item.branch || null,
        position: item.position || null,
        match_score: Math.min(0.99, Math.max(0.3, item.match_score || 0.5)),
        competencies: item.competencies || [],
        research_interests: item.research_interests || item.research_fields || [],
        bio: item.bio || '',
        compatibility_type: item.compatibility_type || 'Потенциальный коллега',
        avatar: item.avatar || null,
      }));

      setRecommendations(formatted);
      try { sessionStorage.setItem(RECOMMENDATIONS_STORAGE_KEY, JSON.stringify({ recommendations: formatted, timestamp: Date.now() })); } catch (_) {}

      // Загружаем всех пользователей, исключая рекомендации
      await loadAllUsers(1, formatted.map(r => r.id));
    } catch (err) {
      console.error('❌ Ошибка загрузки рекомендаций:', err);
      setError('Не удалось загрузить рекомендации');
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔥 Загрузка всех пользователей с фильтрацией на бэкенде
  const loadAllUsers = async (page = 1, excludeIds = []) => {
    setLoadingAllUsers(true);
    try {
      const filters = {
        excludeIds: excludeIds.length > 0 ? excludeIds.join(',') : '',
        search: searchQuery,
        branchId: branchFilter,
        role: roleFilter !== 'all' ? roleFilter : '',
      };

      // Ищем ID компетенций по названиям
      if (selectedCompetencies.length > 0) {
        filters.competencyIds = selectedCompetencies
          .map(name => competenciesFromDB.find(c => c.name === name)?.id)
          .filter(Boolean);
      }

      // Ищем ID научных интересов по названиям
      if (selectedInterests.length > 0) {
        filters.researchFieldIds = selectedInterests
          .map(name => researchFieldsFromDB.find(f => f.name === name)?.id)
          .filter(Boolean);
      }

      const response = await authAPI.getAllUsers(page, 6, filters);

      const formattedUsers = (response.users || []).map(user => ({
        ...user,
        avatar: user.avatar_url,
        branch: user.branch_name,
        competencies: user.competencies || [],
        research_interests: user.research_interests || []
      }));

      setAllUsers(formattedUsers);
      setAllUsersTotalPages(response.totalPages || 1);
      setAllUsersCount(response.count || 0);
      setAllUsersPage(page);
    } catch (err) {
      console.error('❌ Ошибка загрузки пользователей:', err);
    } finally {
      setLoadingAllUsers(false);
    }
  };

  // 🔥 Перезагрузка при изменении фильтров или поиска
  useEffect(() => {
    if (!checkingProfile && !needsQuestionnaire) {
      const excludeIds = recommendations.map(r => r.id);
      loadAllUsers(1, excludeIds);
    }
  }, [searchQuery, branchFilter, roleFilter, selectedCompetencies, selectedInterests]);

  // Сброс фильтров
  const clearFilters = () => {
    setBranchFilter('');
    setRoleFilter('all');
    setSelectedCompetencies([]);
    setSelectedInterests([]);
    setSearchQuery('');
  };

  // Пагинация
  const handlePageChange = (event, value) => {
    setAllUsersPage(value);
    const excludeIds = recommendations.map(r => r.id);
    loadAllUsers(value, excludeIds);
  };

  // Отображение рекомендаций
  const handleShowAllRecommendations = () => setShowAllRecommendations(true);

  const handleOpenInviteModal = (user) => {
    setSelectedUser(user);
    setInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false);
    setSelectedUser(null);
  };

  // Проверка профиля
  useEffect(() => {
    const checkProfile = async () => {
      try {
        const userData = await authAPI.getProfile();
        const hasComp = userData.competencies && Object.keys(userData.competencies).length > 0;
        if (!hasComp) {
          setNeedsQuestionnaire(true);
        } else {
          loadRecommendations();
        }
      } catch (err) {
        console.error('❌ Ошибка проверки профиля:', err);
        setError('Не удалось проверить профиль');
      } finally {
        setCheckingProfile(false);
      }
    };
    checkProfile();
  }, []);

  // Сортировка рекомендаций (локальная — только для рекомендаций)
  const sortedRecommendations = useMemo(() => {
    if (sortBy === 'match') {
      return [...recommendations].sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
    }
    return recommendations;
  }, [recommendations, sortBy]);

  const displayedRecommendations = showAllRecommendations
    ? sortedRecommendations
    : sortedRecommendations.slice(0, 6);

  const hiddenRecommendationsCount = sortedRecommendations.length - 6;

  // ============================================
  // РЕНДЕРИНГ
  // ============================================

  if (checkingProfile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>Проверяем ваш профиль...</Typography>
      </Container>
    );
  }

  if (needsQuestionnaire) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, background: 'linear-gradient(135deg, #284b63 0%, #3c6e71 50%, #58b6b9 100%)', color: 'white' }}>
          <AutoAwesomeIcon sx={{ fontSize: 80, mb: 3, opacity: 0.9 }} />
          <Typography variant="h3" gutterBottom fontWeight="700">Добро пожаловать!</Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>Заполните анкету, чтобы получать рекомендации</Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/questionnaire/3')}
            sx={{ py: 2, px: 6, borderRadius: 4, bgcolor: 'white', color: '#284b63' }}>
            Заполнить анкету
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <PageContainer>
      <AnimatedHeader>
        <CubesContainer>
          {cubes.map((cube, index) => (
            <Cube key={index} left={cube.left} size={cube.size} delay={cube.delay} />
          ))}
        </CubesContainer>
        <HeaderContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
            <Typography variant="h3" fontWeight="900" sx={{ fontFamily: '"Montserrat", sans-serif', letterSpacing: '-0.5px', opacity: '0.9', fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }, textAlign: 'left' }}>
              НАУЧНОЕ КОММЬЮНИТИ
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: '"Noto Sans", sans-serif', fontWeight: 100, fontSize: 18, opacity: 0.7, textAlign: 'left', mt: 1 }}>
              {recommendations.length} рекомендованных коллег · {allUsersCount} пользователей найдено
            </Typography>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <StatChip elevation={0}>
                  <GroupsIcon fontSize="small" />
                  <Typography variant="body2">Всего: {allUsersCount}</Typography>
                </StatChip>
                <StatChip elevation={0}>
                  <StarIcon fontSize="small" />
                  <Typography variant="body2">Рекомендации: {recommendations.length}</Typography>
                </StatChip>
              </Box>
            </Box>
          </Box>
        </HeaderContent>
      </AnimatedHeader>

      {/* Поиск и фильтры */}
      <FilterSection elevation={0}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            placeholder="Поиск по имени, университету, навыкам, интересам..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ flex: 2, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'primary.main' }} /></InputAdornment>,
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}><ClearIcon /></IconButton>
                </InputAdornment>
              )
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
            <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setFiltersOpen(true)}
              sx={{ borderRadius: '16px', minWidth: 120, minHeight: 42, textTransform: 'none', fontWeight: 600, background: alpha(theme.palette.background.paper, 0.55), backdropFilter: 'blur(12px)', borderColor: alpha(theme.palette.primary.main, 0.28), '&:hover': { background: alpha(theme.palette.primary.main, 0.07) } }}>
              Фильтры
            </Button>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Сортировать</InputLabel>
              <Select value={sortBy} label="Сортировать" onChange={(e) => setSortBy(e.target.value)} sx={{ borderRadius: 3 }}>
                <MenuItem value="match">По совместимости</MenuItem>
                <MenuItem value="name">По имени</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Обновить">
              <IconButton onClick={() => loadRecommendations()} disabled={loading}
                sx={{ borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.3), color: 'primary.main' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </FilterSection>

      {/* Панель фильтров */}
      <Drawer anchor="right" open={filtersOpen} onClose={() => setFiltersOpen(false)}
        PaperProps={{ sx: {
          width: { xs: 'min(100vw, 420px)', sm: 420 }, maxWidth: '100vw', boxSizing: 'border-box', p: 0, overflow: 'hidden',
          borderRadius: { xs: '24px 0 0 24px', sm: '28px 0 0 28px' }, border: '1px solid rgba(255,255,255,0.62)',
          background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(145deg, rgba(28,32,38,0.94), rgba(22,25,31,0.88))' : 'linear-gradient(145deg, rgba(255,255,255,0.94), rgba(245,248,250,0.88))',
          backdropFilter: 'blur(28px) saturate(180%)', WebkitBackdropFilter: 'blur(28px) saturate(180%)', boxShadow: '-24px 0 70px rgba(15,23,42,0.20)',
          display: 'flex', flexDirection: 'column'
        } }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0, borderBottom: '1px solid', borderColor: 'divider', background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.48)' }}>
          <Box><Typography variant="h5" fontWeight={700} color="primary">Фильтры</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>Настройте рекомендации</Typography></Box>
          <IconButton onClick={() => setFiltersOpen(false)}><CloseIcon /></IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1, mt: 0 }}>Филиал</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} sx={{ borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }}>
            <MenuItem value="">Все филиалы</MenuItem>
            {branchesFromDB.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1, mt: 2 }}>Роль / Должность</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} sx={{ borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.28)' }}>
            <MenuItem value="all">Все роли</MenuItem>
            <MenuItem value="student">Студенты</MenuItem>
            <MenuItem value="teacher">Преподаватели</MenuItem>
            <MenuItem value="researcher">Научные сотрудники</MenuItem>
            <MenuItem value="professor">Профессора</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1, mt: 2 }}>Компетенции</Typography>
        <Autocomplete
          multiple
          options={competenciesFromDB.map(c => c.name)}
          value={selectedCompetencies}
          onChange={(e, newValue) => setSelectedCompetencies(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => <Chip key={option} label={option} size="small" sx={{ borderRadius: 2, mb: 0.5 }} {...getTagProps({ index })} />)
          }
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Выберите компетенции" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />}
        />

        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1, mt: 2 }}>Научные интересы</Typography>
        <Autocomplete
          multiple
          options={researchFieldsFromDB.map(f => f.name)}
          value={selectedInterests}
          onChange={(e, newValue) => setSelectedInterests(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => <Chip key={option} label={option} size="small" sx={{ borderRadius: 2, mb: 0.5 }} {...getTagProps({ index })} />)
          }
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Выберите научные интересы" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />}
        />

        </Box>
        <Box sx={{ p: 2.5, mt: 'auto', display: 'flex', gap: 1.5, borderTop: '1px solid', borderColor: 'divider', background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.035)' : 'rgba(255,255,255,0.52)' }}>
          <Button fullWidth variant="outlined" onClick={clearFilters} sx={{ borderRadius: '15px', py: 1.2, textTransform: 'none' }}>Сбросить</Button>
          <Button fullWidth variant="contained" onClick={() => setFiltersOpen(false)} sx={{ borderRadius: '15px', py: 1.2, textTransform: 'none', boxShadow: 'none' }}>Применить</Button>
        </Box>
      </Drawer>

      {error && <Alert severity="error" sx={{ maxWidth: 960, width: '100%', mb: 3, borderRadius: 3 }}>{error}</Alert>}

      {/* Рекомендации */}
      {(loading || sortedRecommendations.length > 0) && (
        <Box sx={{ width: '100%', maxWidth: 960, mb: 4 }}>
          <SectionTitle><StarIcon sx={{ color: theme.palette.warning.main }} />Рекомендованные коллеги</SectionTitle>
          {loading ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {Array.from(new Array(6)).map((_, i) => <SkeletonCard key={i} variant="recommendation" />)}
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {displayedRecommendations.map((user) => (
                  <Box key={user.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' }, maxWidth: 300, display: 'flex', justifyContent: 'center' }}>
                    <UserCard user={user} onInvite={handleOpenInviteModal} variant="recommendation" />
                  </Box>
                ))}
              </Box>
              {sortedRecommendations.length > 6 && !showAllRecommendations && (
                <ShowMoreButton variant="outlined" onClick={handleShowAllRecommendations} startIcon={<ChevronRightIcon />}>
                  Показать еще {hiddenRecommendationsCount} {getWordForm(hiddenRecommendationsCount, ['рекомендацию', 'рекомендации', 'рекомендаций'])}
                </ShowMoreButton>
              )}
            </>
          )}
        </Box>
      )}

      {/* Все пользователи */}
      <Box sx={{ width: '100%', maxWidth: 960 }}>
        <SectionTitle><GroupsIcon sx={{ color: theme.palette.primary.main }} />Все пользователи</SectionTitle>
        {loadingAllUsers ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {Array.from(new Array(6)).map((_, i) => <SkeletonCard key={i} variant="simple" />)}
          </Box>
        ) : allUsers.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {allUsers.map((user) => (
                <Box key={user.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.333% - 16px)' }, maxWidth: 300, display: 'flex', justifyContent: 'center' }}>
                  <UserCard user={user} onInvite={handleOpenInviteModal} variant="simple" />
                </Box>
              ))}
            </Box>
            {allUsersTotalPages > 1 && (
              <PaginationContainer>
                <Pagination count={allUsersTotalPages} page={allUsersPage} onChange={handlePageChange} color="primary" size="large" showFirstButton showLastButton
                  sx={{ '& .MuiPaginationItem-root': { borderRadius: 2 } }} />
              </PaginationContainer>
            )}
          </>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4 }}>
            <GroupsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {searchQuery || branchFilter || roleFilter !== 'all' ? 'Нет пользователей по вашему запросу' : 'Нет других пользователей'}
            </Typography>
          </Paper>
        )}
      </Box>

      <InviteModal open={inviteModalOpen} onClose={handleCloseInviteModal} user={selectedUser} onSuccess={loadRecommendations} />
    </PageContainer>
  );
};

const getWordForm = (number, forms) => {
  const cases = [2, 0, 1, 1, 1, 2];
  return forms[(number % 100 > 4 && number % 100 < 20) ? 2 : cases[Math.min(number % 10, 5)]];
};

export default RecommendationsPage;